// Intro for new players: one round of the game in seven scenes, on the design boards' look.
import React, { useEffect, useState } from 'react'
import * as S from './style'
import { Wordmark } from '../../containers/Home/parts'
import { t } from '../../i18n'

// animation-delay, one value per animation the element runs
const at = (...seconds) => ({ animationDelay: seconds.map(s => `${s}s`).join(', ') })

// The demo cards and players come from the catalog at render time.
const hand = () => [t('intro.hand1'), t('intro.hand2'), t('intro.hand3'), t('intro.hand4')]
const PICKED = 2
const submissions = () => { const h = hand(); return [h[0], h[PICKED], h[1]] }
const WINNER = 1
const players = () => [t('intro.player1'), t('intro.player2'), t('intro.player3'), t('intro.player4')]

const Caption = ({ title, text, delay = 0 }) => (
  <S.Rise style={at(delay)}>
    {title && <h2 className="h-l">{title}</h2>}
    {text && <p style={{ margin: '8px 0 0', font: '400 16px/1.5 var(--sans)' }}>{text}</p>}
  </S.Rise>
)

const Welcome = () => (
  <>
    <Caption title={t('intro.welcome')}/>
    <S.Fan aria-hidden="true">
      <S.FanCard $dark style={at(0.3)}/>
      <S.FanCard style={at(0.4)}>
        <span style={{ width: 44, background: 'var(--salmon)' }}></span>
        <span style={{ width: 30, background: 'var(--ink)' }}></span>
      </S.FanCard>
    </S.Fan>
    <S.Pop style={{ ...at(0.9), font: '400 64px/1 var(--display)' }}><Wordmark accent="var(--mint)" /></S.Pop>
    <Caption text={t('intro.howTo')} delay={1.4}/>
  </>
)

const Judge = () => (
  <>
    <Caption title={t('intro.judgeTitle')} text={t('intro.judgeText')}/>
    <S.Deal className="bc" style={at(0.8)}>
      <p className="bc-text">{t('intro.question')} <span className="blank"></span>.</p>
      <div className="bc-foot"><span className="pick">{1}</span><span>{t('app.title')}</span></div>
    </S.Deal>
  </>
)

const Choose = () => (
  <>
    <Caption title={t('intro.chooseTitle')} text={t('intro.chooseText')}/>
    <S.Deal className="bc bc-sm" style={at(0.2)}>
      <p className="bc-text">
        {t('intro.question')}{' '}
        <S.Slot>
          <S.FadeOut className="blank" style={at(2.6)}/>
          <S.FadeIn className="fill" style={at(2.7)}>{hand()[PICKED]}</S.FadeIn>
        </S.Slot>.
      </p>
    </S.Deal>
    <div className="hand" style={{ width: '100%' }}>
      {hand().map((text, i) => i === PICKED
        ? <S.Picked key={text} className="wc wc-sel" style={at(0.6 + i * 0.06, 1.8)}>
            <S.Pop as="span" className="badge" style={at(1.8)}>{1}</S.Pop>{text}
          </S.Picked>
        : <S.Rise key={text} className="wc" style={at(0.6 + i * 0.06)}>{text}</S.Rise>)}
    </div>
  </>
)

const Pick = () => (
  <>
    <Caption title={t('intro.pickTitle')} text={t('intro.pickText')}/>
    <ol className="subs" style={{ width: '100%', listStyle: 'none', margin: 0, padding: 0 }}>
      {submissions().map((text, i) => {
        const card = <div className="bc bc-sm" style={i === WINNER ? at(2.2) : undefined}>
          <p className="bc-text">{t('intro.question')} <span className="fill">{text}</span>.</p>
        </div>
        return i === WINNER
          ? <S.Winner key={text} className="sub sub-win" style={at(0.6 + i * 0.12)}>
              <S.Pop as="span" className="trophy" role="img" aria-label={t('game.winner')} style={{ ...at(2.3), zIndex: 1 }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M7 4h10v4a5 5 0 0 1-10 0z"></path><path d="M7 6H4a3 3 0 0 0 3 4M17 6h3a3 3 0 0 1-3 4M12 13v4M8 20h8"></path></svg>
              </S.Pop>
              {card}
            </S.Winner>
          : <S.Dimmed key={text} className="sub sub-off" style={at(0.6 + i * 0.12, 2.2)}>{card}</S.Dimmed>
      })}
    </ol>
    <S.Pop style={{ ...at(2.7), display: 'flex', alignItems: 'center', gap: 10, font: '500 18px var(--sans)' }}>
      {t('intro.point', { name: players()[1] })}
      <span style={{ background: 'var(--mint)', border: '2px solid var(--line)', borderRadius: 999, padding: '4px 12px', font: '700 16px var(--sans)', direction: 'ltr' }}>+{1}</span>
    </S.Pop>
  </>
)

const NextJudge = () => (
  <>
    <Caption title={t('intro.nextTitle')} text={t('intro.nextText')}/>
    <div className="chips" style={{ justifyContent: 'center' }}>
      {players().map((name, i) => {
        const Chip = i === 0 ? S.ChipOff : i === 1 ? S.ChipOn : 'span'
        return (
          <S.Rise key={name} style={{ ...at(0.5 + i * 0.08), display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
            <Chip className={i === 1 ? 'chip chip-on' : 'chip'} style={i < 2 ? at(1.6) : undefined}>{name}</Chip>
            {i === 0 && <S.FadeOut className="sec" style={at(1.6)}>{t('intro.judge')}</S.FadeOut>}
            {i === 1 && <S.Pop as="span" className="sec" style={{ ...at(1.8), color: 'var(--prompt)' }}>{t('intro.judge')}</S.Pop>}
          </S.Rise>
        )
      })}
    </div>
  </>
)

const Rando = () => (
  <>
    <Caption title={t('intro.randoTitle')} text={t('intro.randoText')}/>
    <S.Pop as="svg" width="150" height="125" viewBox="0 0 180 150" aria-hidden="true" style={at(0.6)}>
      <g transform="rotate(-14 62 72)">
        <rect x="20" y="30" width="84" height="84" rx="16" fill="#ee5a4a" stroke="#fff6e0" strokeWidth="3"></rect>
        <circle cx="41" cy="51" r="7" fill="#24102a"></circle>
        <circle cx="62" cy="72" r="7" fill="#24102a"></circle>
        <circle cx="83" cy="93" r="7" fill="#24102a"></circle>
      </g>
      <g transform="rotate(12 131 79)">
        <rect x="96" y="44" width="70" height="70" rx="14" fill="#7fe0b5" stroke="#fff6e0" strokeWidth="3"></rect>
        <circle cx="114" cy="62" r="6" fill="#24102a"></circle>
        <circle cx="148" cy="62" r="6" fill="#24102a"></circle>
        <circle cx="114" cy="96" r="6" fill="#24102a"></circle>
        <circle cx="148" cy="96" r="6" fill="#24102a"></circle>
      </g>
    </S.Pop>
    <S.Tally role="img" aria-label={t('intro.randoTally')}>
      {[1.4, 1.7, 2.0].map(s => <span key={s} style={at(s)}></span>)}
    </S.Tally>
    <S.Fatality style={at(2.6)}>
      <strong style={{ font: '400 52px/1 var(--display)', color: 'var(--salmon)' }}>{t('intro.randoWon')}</strong>
      <span style={{ font: '400 36px/1 var(--display)', color: 'var(--mint)' }}>{t('end.fatality')}</span>
    </S.Fatality>
  </>
)

const CONFETTI = [
  { x: 8, y: 12, r: -24, c: 'var(--mint)' }, { x: 78, y: 8, r: 18, c: 'var(--salmon)' },
  { x: 4, y: 52, r: 30, c: 'var(--salmon)' }, { x: 84, y: 56, r: -30, c: 'var(--mint)' },
  { x: 30, y: 4, round: true, c: 'var(--surface)' }, { x: 66, y: 30, round: true, c: 'var(--mint)' },
  { x: 14, y: 30, round: true, c: 'var(--surface)' },
]

const Ready = ({ onDone }) => (
  <>
    {CONFETTI.map((p, i) => <S.Confetti key={i} aria-hidden="true" $x={p.x} $y={p.y} $r={p.r} $c={p.c} $round={p.round}/>)}
    <S.Pop as="h1" style={{ ...at(0.2), margin: 0, font: '400 84px/1 var(--display)', color: 'var(--mint)', transform: 'rotate(-4deg)' }}>{t('intro.ready')}</S.Pop>
    <Caption text={t('intro.readyText')} delay={0.5}/>
    <S.Pop as="button" className="btn btn-primary" onClick={onDone}
           style={{ ...at(0.8), padding: '0 40px', borderColor: 'var(--surface)', boxShadow: '0 4px 0 var(--surface)' }}>{t('intro.play')}</S.Pop>
  </>
)

// duration in ms (a tap skips the wait); the last scene stays until the player taps the button
const SCENES = [
  { Body: Welcome, duration: 3500, background: '#4b1d52', color: '#fff6e0' },
  { Body: Judge, duration: 4000 },
  { Body: Choose, duration: 5500 },
  { Body: Pick, duration: 5500 },
  { Body: NextJudge, duration: 4000 },
  { Body: Rando, duration: 5500, background: '#24102a', color: '#fff6e0' },
  { Body: Ready, background: '#4b1d52', color: '#ffffff' },
]

export default function Intro({ onDone = () => {} }) {
  const [scene, setScene] = useState(0)
  const { Body, duration, background, color } = SCENES[scene]

  useEffect(() => {
    if (!duration) return
    const timer = setTimeout(() => setScene(scene + 1), duration)
    return () => clearTimeout(timer)
  }, [scene, duration])

  return (
    <S.Stage className="screen" style={{ background, color }}>
      {duration && <S.Skip className="btn btn-sm" onClick={onDone}>{t('intro.skip')}</S.Skip>}
      {/* a tap anywhere on the scene moves on; the timer restarts for the new scene */}
      <S.Scene key={scene} onClick={duration ? () => setScene(scene + 1) : undefined}
               style={duration ? { cursor: 'pointer' } : undefined}>
        <Body onDone={onDone}/>
      </S.Scene>
      <S.Dots aria-hidden="true">
        {SCENES.map((_, i) => <span key={i} className={i === scene ? 'on' : undefined}></span>)}
      </S.Dots>
    </S.Stage>
  )
}
